import React, { useState, useEffect } from 'react';
import Toggle from '../../../components/ui/Toggle';
import { useAuth } from '../../../context/AuthContext';
import { useError } from '../../../context/ErrorContext';
import Layout from '../../../components/layout/Layout';
import PestanasModulo from '../../../components/common/PestanasModulo';
import { PAGOS_TABS } from '../pestanas';
import { CreditCard, Save, Check, Building, DollarSign, RefreshCw, Info } from 'lucide-react';
import { PAYMENT_METHOD_CATALOG } from '../../subscription/components/PaymentMethodsSelector';
import { billingApi, type BankAccountInfo } from '../../../api/billingApi';

const PaymentMethodSettingsPage: React.FC = () => {
  const { currentUser } = useAuth();
  const { addError, handleFirebaseError, clearErrors } = useError();

  const [bankAccountInfo, setBankAccountInfo] = useState<BankAccountInfo>({
    bank: '',
    accountName: '',
    accountNumber: '',
    clabe: '',
    beneficiaryId: '',
    bankBranch: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  // Config de métodos activos (system/payment_settings.methods). true/false por id.
  const [methodsCfg, setMethodsCfg] = useState<Record<string, boolean>>({});
  const [methodSaving, setMethodSaving] = useState<string | null>(null);

  // Fetch payment settings when component mounts
  useEffect(() => {
    const fetchPaymentSettings = async () => {
      if (!currentUser?.uid || currentUser.role !== 'admin') {
        return;
      }

      try {
        setLoading(true);
        if (clearErrors) clearErrors();

        const { bankAccount, methods } = await billingApi.getPaymentSettings();
        if (bankAccount) {
          setBankAccountInfo({
            bank: bankAccount.bank || '',
            accountName: bankAccount.accountName || '',
            accountNumber: bankAccount.accountNumber || '',
            clabe: bankAccount.clabe || '',
            beneficiaryId: bankAccount.beneficiaryId || '',
            bankBranch: bankAccount.bankBranch || ''
          });
        }
        if (methods) {
          setMethodsCfg(methods);
        }
      } catch (error) {
        console.error('Error fetching payment settings:', error);
        if (handleFirebaseError) handleFirebaseError(error, 'fetch-payment-settings');
      } finally {
        setLoading(false);
      }
    };

    fetchPaymentSettings();
  }, [currentUser?.uid, currentUser?.role, clearErrors, handleFirebaseError]);

  // Prender/apagar un método de pago (guarda al instante, sin tocar el form bancario)
  const handleToggleMethod = async (id: string, value: boolean) => {
    if (!currentUser?.uid || currentUser.role !== 'admin') return;
    const prev = methodsCfg;
    const next = { ...methodsCfg, [id]: value };
    setMethodsCfg(next);
    try {
      setMethodSaving(id);
      if (clearErrors) clearErrors();
      await billingApi.savePaymentSettings({ methods: next });
    } catch (error) {
      console.error('Error toggling payment method:', error);
      setMethodsCfg(prev); // revertir si falla
      if (handleFirebaseError) handleFirebaseError(error, 'toggle-payment-method');
    } finally {
      setMethodSaving(null);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setBankAccountInfo(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async () => {
    if (!currentUser?.uid || currentUser.role !== 'admin') {
      if (addError) addError('No tienes permisos para realizar esta acción', 'permission', 'payment-settings');
      return;
    }

    // Basic validation
    // Número de cuenta es opcional: para transferir basta la CLABE (y el dueño no la ve si va vacía)
    if (!bankAccountInfo.bank || !bankAccountInfo.accountName || !bankAccountInfo.clabe) {
      if (addError) addError('Por favor completa los campos obligatorios', 'validation', 'payment-settings-required');
      return;
    }

    try {
      setSaving(true);
      if (clearErrors) clearErrors();
      setSuccess(false);

      // Merge en la base (server actualiza bank_account de payment_settings).
      await billingApi.savePaymentSettings({ bankAccount: bankAccountInfo });

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error('Error saving payment settings:', error);
      if (handleFirebaseError) handleFirebaseError(error, 'save-payment-settings');
    } finally {
      setSaving(false);
    }
  };

  if (!currentUser || currentUser.role !== 'admin') {
    return (
      <Layout>
        <div className="text-center text-red-600 dark:text-red-400 p-4">
          No tienes permiso para acceder a esta página.
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
          <h1 className="text-2xl font-bold text-content flex items-center">
            <CreditCard className="h-6 w-6 mr-2 text-accent" />
            Pagos
          </h1>
          <p className="mt-1 text-sm text-content-2">
            Ajustes de cobro: métodos de pago activos y cuenta bancaria del pago manual
          </p>
        </div>
        <PestanasModulo tabs={PAGOS_TABS} activa="ajustes" testid="pagos-tabs" />

        {success && (
          <div className="bg-green-50 dark:bg-green-500/10 border-l-4 border-green-400 p-4 animate-fade-in-up" style={{ animationDelay: '0.15s' }}>
            <div className="flex">
              <div className="flex-shrink-0">
                <Check className="h-5 w-5 text-green-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-green-700 dark:text-green-300">
                  Configuración guardada correctamente
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="bg-surface shadow rounded-lg overflow-hidden animate-fade-in-up" style={{ animationDelay: '0.15s' }}>
          <div className="px-4 py-5 sm:p-6">
            {loading ? (
              <div className="flex justify-center items-center h-64">
                <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
                <span className="ml-3 text-content-2">Cargando configuración...</span>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Métodos de pago activos */}
                <div>
                  <h3 className="text-lg font-medium text-content flex items-center mb-1">
                    <CreditCard className="h-5 w-5 mr-2 text-content-3" />
                    Métodos de pago activos
                  </h3>
                  <p className="text-sm text-content-3 mb-4">
                    Prende o apaga lo que ven tus clientes al pagar. Se guarda al instante.
                  </p>
                  <div className="space-y-2">
                    {PAYMENT_METHOD_CATALOG.map((m) => {
                      const enabled = m.implemented && methodsCfg[m.id] !== false;
                      const Icon = m.icon;
                      return (
                        <div
                          key={m.id}
                          className="flex items-center justify-between p-3 border border-line rounded-lg"
                        >
                          <div className="flex items-center min-w-0">
                            <div className="h-9 w-9 flex-shrink-0 flex items-center justify-center rounded-full bg-canvas mr-3">
                              <Icon className="h-5 w-5 text-content-2" />
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-content">{m.name}</span>
                                {!m.implemented && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-500/20 text-yellow-800 dark:text-yellow-300">
                                    Próximamente
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-content-3 truncate">{m.description}</p>
                            </div>
                          </div>
                          <div className="ml-3 flex-shrink-0 flex items-center">
                            {methodSaving === m.id && (
                              <RefreshCw className="h-4 w-4 mr-2 animate-spin text-content-3" />
                            )}
                            <Toggle
                              checked={enabled}
                              onChange={(v) => handleToggleMethod(m.id, v)}
                              disabled={!m.implemented || methodSaving === m.id}
                              label={`Activar ${m.name ?? m.id}`}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="border-t border-line" />

                {/* Information Panel */}
                <div className="bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/30 p-4 rounded-md">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <Info className="h-5 w-5 text-blue-400" />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-accent">Información de pago manual</h3>
                      <p className="text-sm text-blue-500 mt-1">
                        Estos datos se mostrarán a los usuarios cuando realicen un pago manual a través de transferencia bancaria.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Bank Account Form */}
                <div>
                  <h3 className="text-lg font-medium text-content flex items-center mb-4">
                    <Building className="h-5 w-5 mr-2 text-content-3" />
                    Datos Bancarios
                  </h3>
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <div>
                      <label htmlFor="bank" className="block text-sm font-medium text-content-2">
                        Banco <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="bank"
                        name="bank"
                        value={bankAccountInfo.bank}
                        onChange={handleInputChange}
                        required
                        className="mt-1 block w-full border border-line rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-accent focus:border-blue-500 sm:text-sm"
                        placeholder="Ej: BBVA Bancomer"
                      />
                    </div>
                    <div>
                      <label htmlFor="accountName" className="block text-sm font-medium text-content-2">
                        Nombre del Beneficiario <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="accountName"
                        name="accountName"
                        value={bankAccountInfo.accountName}
                        onChange={handleInputChange}
                        required
                        className="mt-1 block w-full border border-line rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-accent focus:border-blue-500 sm:text-sm"
                        placeholder="Ej: Lealify SA de CV"
                      />
                    </div>
                    <div>
                      <label htmlFor="accountNumber" className="block text-sm font-medium text-content-2">
                        Número de Cuenta <span className="text-content-3 font-normal">(opcional)</span>
                      </label>
                      <input
                        type="text"
                        id="accountNumber"
                        name="accountNumber"
                        value={bankAccountInfo.accountNumber}
                        onChange={handleInputChange}
                        className="mt-1 block w-full border border-line rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-accent focus:border-blue-500 sm:text-sm"
                        placeholder="Ej: 0123456789"
                      />
                    </div>
                    <div>
                      <label htmlFor="clabe" className="block text-sm font-medium text-content-2">
                        CLABE Interbancaria <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="clabe"
                        name="clabe"
                        value={bankAccountInfo.clabe}
                        onChange={handleInputChange}
                        required
                        className="mt-1 block w-full border border-line rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-accent focus:border-blue-500 sm:text-sm"
                        placeholder="Ej: 012345678901234567"
                      />
                    </div>
                    <div>
                      <label htmlFor="beneficiaryId" className="block text-sm font-medium text-content-2">
                        RFC o Identificación Fiscal
                      </label>
                      <input
                        type="text"
                        id="beneficiaryId"
                        name="beneficiaryId"
                        value={bankAccountInfo.beneficiaryId}
                        onChange={handleInputChange}
                        className="mt-1 block w-full border border-line rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-accent focus:border-blue-500 sm:text-sm"
                        placeholder="Ej: ABC123456XYZ"
                      />
                    </div>
                    <div>
                      <label htmlFor="bankBranch" className="block text-sm font-medium text-content-2">
                        Sucursal Bancaria
                      </label>
                      <input
                        type="text"
                        id="bankBranch"
                        name="bankBranch"
                        value={bankAccountInfo.bankBranch}
                        onChange={handleInputChange}
                        className="mt-1 block w-full border border-line rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-accent focus:border-blue-500 sm:text-sm"
                        placeholder="Ej: Sucursal Principal"
                      />
                    </div>
                  </div>
                </div>

                {/* Preview Section */}
                <div className="bg-surface-2 p-4 rounded-lg border border-line">
                  <h3 className="text-sm font-medium text-content-2 mb-3">Vista previa para el usuario</h3>
                  <div className="bg-blue-50 dark:bg-blue-500/10 border-l-4 border-blue-400 p-3 rounded-md">
                    <div className="flex items-center">
                      <DollarSign className="h-5 w-5 text-blue-500 mr-2" />
                      <h4 className="text-sm font-medium text-blue-700 dark:text-blue-300">Información de pago</h4>
                    </div>
                    <ul className="mt-2 space-y-1 list-disc pl-10 text-sm text-blue-700 dark:text-blue-300">
                      <li><span className="font-medium">Banco:</span> {bankAccountInfo.bank || '[Banco]'}</li>
                      <li><span className="font-medium">Beneficiario:</span> {bankAccountInfo.accountName || '[Nombre]'}</li>
                      {bankAccountInfo.accountNumber && <li><span className="font-medium">Cuenta:</span> {bankAccountInfo.accountNumber}</li>}
                      <li><span className="font-medium">CLABE:</span> {bankAccountInfo.clabe || '[CLABE]'}</li>
                      {bankAccountInfo.beneficiaryId && (
                        <li><span className="font-medium">RFC:</span> {bankAccountInfo.beneficiaryId}</li>
                      )}
                      {bankAccountInfo.bankBranch && (
                        <li><span className="font-medium">Sucursal:</span> {bankAccountInfo.bankBranch}</li>
                      )}
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="px-4 py-4 sm:px-6 bg-surface-2 flex justify-end">
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || loading}
              style={{ backgroundColor: 'rgb(var(--c-accent))' }}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent disabled:opacity-50"
            >
              {saving ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Guardando...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Guardar Cambios
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default PaymentMethodSettingsPage;